import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

const adminAPI = axios.create({
  baseURL: `${API_URL}/admin`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
adminAPI.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('admin_access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle token refresh
adminAPI.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('admin_refresh_token');
        const response = await axios.post(`${API_URL}/admin/auth/refresh`, {}, {
          headers: {
            Authorization: `Bearer ${refreshToken}`,
          },
        });

        const { access_token } = response.data;
        localStorage.setItem('admin_access_token', access_token);

        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        return adminAPI(originalRequest);
      } catch (refreshError) {
        localStorage.removeItem('admin_access_token');
        localStorage.removeItem('admin_refresh_token');
        window.location.href = '/admin/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const adminAuthAPI = {
  login: (data) => adminAPI.post('/auth/login', data),
  logout: () => adminAPI.post('/auth/logout'),
  getCurrentAdmin: () => adminAPI.get('/auth/me'),
  changePassword: (data) => adminAPI.put('/auth/change-password', data),
  forgotPassword: (data) => adminAPI.post('/password-reset/forgot-password', data),
  verifyResetToken: (data) => adminAPI.post('/password-reset/verify-reset-token', data),
  resetPassword: (data) => adminAPI.post('/password-reset/reset-password', data),
};

// Users API
export const adminUsersAPI = {
  getAll: (params) => adminAPI.get('/users', { params }),
  getById: (id) => adminAPI.get(`/users/${id}`),
  create: (data) => adminAPI.post('/users', data),
  update: (id, data) => adminAPI.put(`/users/${id}`, data),
  delete: (id) => adminAPI.delete(`/users/${id}`),
  activate: (id) => adminAPI.post(`/users/${id}/activate`),
  deactivate: (id) => adminAPI.post(`/users/${id}/deactivate`),
  getStats: () => adminAPI.get('/users/stats'),
};

// Companies API
export const adminCompaniesAPI = {
  getAll: () => adminAPI.get('/companies'),
  getById: (id) => adminAPI.get(`/companies/${id}`),
  create: (data) => adminAPI.post('/companies', data),
  update: (id, data) => adminAPI.put(`/companies/${id}`, data),
  delete: (id) => adminAPI.delete(`/companies/${id}`),
};

// Donations API
export const adminDonationsAPI = {
  getAllCauses: () => adminAPI.get('/donations/causes'),
  getCause: (id) => adminAPI.get(`/donations/causes/${id}`),
  createCause: (data) => adminAPI.post('/donations/causes', data),
  updateCause: (id, data) => adminAPI.put(`/donations/causes/${id}`, data),
  deleteCause: (id) => adminAPI.delete(`/donations/causes/${id}`),
  getAllDonations: (params) => adminAPI.get('/donations/donations', { params }),
  getStats: () => adminAPI.get('/donations/stats'),
};

// Stats API
export const adminStatsAPI = {
  getDashboard: () => adminAPI.get('/stats/dashboard'),
  getUserGrowth: (days) => adminAPI.get('/stats/users/growth', { params: { days } }),
  getRecentActivity: (limit) => adminAPI.get('/stats/activity/recent', { params: { limit } }),
};

// Audit API
export const adminAuditAPI = {
  getLogs: (params) => adminAPI.get('/audit', { params }),
  getActions: () => adminAPI.get('/audit/actions'),
  getAdmins: () => adminAPI.get('/audit/admins'),
  getStats: () => adminAPI.get('/audit/stats'),
};

// System Config API
export const adminConfigAPI = {
  getAll: (params) => adminAPI.get('/config', { params }),
  getByKey: (key) => adminAPI.get(`/config/${key}`),
  update: (key, data) => adminAPI.put(`/config/${key}`, data),
  bulkUpdate: (data) => adminAPI.put('/config/bulk', data),
  create: (data) => adminAPI.post('/config', data),
  delete: (key) => adminAPI.delete(`/config/${key}`),
};

// Admin Subscriptions API
export const adminSubscriptionsAPI = {
  getAll: (params) => adminAPI.get('/subscriptions', { params }),
  getStats: () => adminAPI.get('/subscriptions/stats'),
  update: (id, data) => adminAPI.put(`/subscriptions/${id}`, data),
  runCheck: () => adminAPI.post('/subscriptions/run-check'),
};

export default adminAPI;
